import type { BarcodeFormat, ItemUnit } from '@erp/shared';

import { formatNumber } from '../../../lib/intl.js';

/**
 * A stock quantity for reading: the API sends DECIMAL strings ("5468.0000"),
 * so whole counts lose their four zeros, real fractions keep up to four places,
 * thousands are grouped. Never used for money.
 */
export function fmtQty(value: string | number | null | undefined, locale: string): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return String(value ?? '');
  return formatNumber(n, { locale }, { maximumFractionDigits: 4 });
}

/** `+12` / `−3` / `0` — a movement's direction is part of the number. */
export function fmtSigned(value: string | number, locale: string): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return fmtQty(0, locale);
  return `${n > 0 ? '+' : '−'}${fmtQty(Math.abs(n), locale)}`;
}

/**
 * The biggest pack a product is sold in — the carton a keeper counts in. Units
 * on sale first; a product whose only packs are hidden still counts in them.
 */
export function biggestPack(units: ItemUnit[]): { name: string; factor: number } | null {
  const usable = (list: ItemUnit[]): Array<{ name: string; factor: number }> => list
    .map((u) => ({ name: u.name, factor: Number(u.factor) }))
    .filter((u) => Number.isFinite(u.factor) && u.factor > 1);
  const pick = usable(units.filter((u) => u.isActive));
  const pool = pick.length > 0 ? pick : usable(units);
  if (pool.length === 0) return null;
  return pool.reduce((a, b) => (b.factor > a.factor ? b : a));
}

/**
 * A base-unit quantity as whole packs and a remainder — 5,468 pieces in
 * cartons of 80 is 68 cartons and 28 pieces. Sign kept on the packs, so a
 * negative shelf reads negative here too. Null when there is no pack.
 */
export function packBreakdown(
  quantity: string | number,
  pack: { name: string; factor: number } | null,
): { negative: boolean; packs: number; rest: number } | null {
  const q = Number(quantity);
  if (!pack || !Number.isFinite(q) || q === 0) return null;
  const abs = Math.abs(q);
  const packs = Math.floor(abs / pack.factor + 1e-9);
  // Rounded to the four places stock is kept in, so 0.1 + 0.2 noise never shows
  // (and never as a "−0" left over from a division that landed a hair short).
  const rest = Math.max(0, Math.round((abs - packs * pack.factor) * 10_000) / 10_000) || 0;
  return { negative: q < 0, packs, rest };
}

/**
 * The symbology a stored code can be drawn in. The item's own primary code has
 * a chosen format; a pack's code or an extra alias does not, so its shape
 * decides — the digit-only formats throw on anything else.
 */
export function guessBarcodeFormat(code: string): BarcodeFormat {
  if (/^\d{13}$/.test(code)) return 'ean13';
  if (/^\d{8}$/.test(code)) return 'ean8';
  if (/^\d{12}$/.test(code)) return 'upc_a';
  return 'code128';
}

/** Start of a local calendar day `daysBack` days before today, as ISO — for date-range chips. */
export function localDayStartIso(daysBack: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysBack);
  return d.toISOString();
}

/** A `YYYY-MM-DD` from an `<input type="date">` as the ISO start / end of that LOCAL day. */
export function dayBoundIso(date: string, edge: 'start' | 'end'): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return undefined;
  const d = edge === 'start'
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0)
    : new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  return d.toISOString();
}

/** Where a movement's reference document lives in the app, if it has a page. */
export function referencePath(referenceType: string | null, referenceId: string | null): string | null {
  if (!referenceType || !referenceId) return null;
  switch (referenceType) {
    case 'invoice': return `/sales/invoices/${referenceId}`;
    case 'bill': return `/purchasing/bills/${referenceId}`;
    case 'stock_transfer': return `/inventory/transfers/${referenceId}`;
    case 'production_order': return `/manufacturing/orders/${referenceId}`;
    default: return null;
  }
}

/** Inline tint for a token colour — `bg-danger/10` emits no CSS on theme variables. */
export function tint(token: 'danger' | 'success' | 'warning' | 'info' | 'brand', percent = 12): { background: string } {
  return { background: `color-mix(in oklab, var(--color-${token}) ${percent}%, transparent)` };
}
